/** Réglages versionnés de l’assistant et consentement explicite aux envois pratiques. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dialog, Input } from '@artosera/ui';
import { CATEGORIES_CONVERSATION, SUJETS_PRATIQUES } from '@artosera/shared';
import { assistantApi, useLectureAssistant, type Reglages } from '../../api/assistant-reponse.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import { useGesteAssistant } from '../boite-reception/useGesteAssistant.js';
import styles from '../boite-reception/Boite.module.css';
import assistantStyles from '../boite-reception/Assistant.module.css';

export function AssistantReponsePage() {
  const { galerie, galerieSlug, compte } = useShellContext();

  if (!galerieAModule(galerie, 'assistant-reponse') || !galerieALaction(galerie, 'inbox:settings'))
    return <NotFoundPage />;

  return <ReglagesAssistant key={`${compte.id}:${galerieSlug}`} />;
}

function ReglagesAssistant() {
  const { galerieSlug } = useShellContext();

  const { t } = useTranslation(['regie', 'commun']);

  const lecture = useLectureAssistant(galerieSlug, 'reglages', (appel) =>
    assistantApi.reglages(galerieSlug, appel),
  );

  const [notice, setNotice] = useState('');

  const enregistrer = useGesteAssistant(
    (r: Reglages & { confirmationAutomatique?: boolean }, appel) =>
      assistantApi.regler(galerieSlug, r, appel),
  );

  return (
    <section className={styles.page}>
      <h1>{t('regie:assistantReponse.reglages')}</h1>
      {lecture.isPending && <p role="status">{t('commun:chargement')}</p>}
      {lecture.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {enregistrer.erreur && <p role="alert">{enregistrer.erreur}</p>}
      {notice && <p role="status">{notice}</p>}
      {lecture.data && (
        <Formulaire
          key={lecture.data.version}
          initial={lecture.data}
          pending={enregistrer.pending}
          sauver={async (r) => {
            setNotice('');

            const resultat = await enregistrer.executer(r);
            if (resultat) setNotice(t('regie:assistantReponse.enregistre'));
            return Boolean(resultat);
          }}
        />
      )}
    </section>
  );
}

function Formulaire({
  initial,
  pending,
  sauver,
}: {
  initial: Reglages;
  pending: boolean;
  sauver: (r: Reglages & { confirmationAutomatique?: boolean }) => Promise<boolean>;
}) {
  const { t } = useTranslation(['regie', 'commun']);

  const [r, setR] = useState(initial);

  const [confirmation, setConfirmation] = useState(false);

  const [terme, setTerme] = useState('');

  const changer = <K extends keyof Reglages>(cle: K, valeur: Reglages[K]) =>
    setR((avant) => ({ ...avant, [cle]: valeur }));

  const sujets = r.reponsesPratiques.sujets
    .map((s) => t(`regie:assistantReponse.sujets.${s}`))
    .join(', ');

  return (
    <>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          if (pending) return;
          if (r.reponsesPratiques.actif && !initial.reponsesPratiques.actif) setConfirmation(true);
          else void sauver(r);
        }}
      >
        <fieldset className={styles.formulaire} disabled={pending}>
          <legend>{t('regie:assistantReponse.reglages')}</legend>
          <Checkbox
            id="assistant-tri"
            label={t('regie:assistantReponse.triActif')}
            checked={r.triActif}
            onCheckedChange={(v) => changer('triActif', v === true)}
          />
          <fieldset className={styles.formulaire}>
            <legend>{t('regie:assistantReponse.brouillonsAuto')}</legend>
            <Checkbox
              id="assistant-brouillons"
              label={t('regie:assistantReponse.brouillonsActifs')}
              checked={r.brouillonsAutomatiques.actif}
              onCheckedChange={(v) =>
                changer('brouillonsAutomatiques', {
                  ...r.brouillonsAutomatiques,
                  actif: v === true,
                })
              }
            />
            {CATEGORIES_CONVERSATION.map((c) => (
              <Checkbox
                key={c}
                id={`assistant-categorie-${c}`}
                label={t(`regie:boiteReception.categories.${c}`)}
                checked={r.brouillonsAutomatiques.categories.includes(c)}
                onCheckedChange={(v) =>
                  changer('brouillonsAutomatiques', {
                    ...r.brouillonsAutomatiques,
                    categories: v
                      ? [...r.brouillonsAutomatiques.categories, c]
                      : r.brouillonsAutomatiques.categories.filter((x) => x !== c),
                  })
                }
              />
            ))}
          </fieldset>
          <Checkbox
            id="assistant-prix"
            label={t('regie:assistantReponse.prixParEcrit')}
            checked={r.prixParEcrit}
            onCheckedChange={(v) => changer('prixParEcrit', v === true)}
          />
          <fieldset className={styles.formulaire}>
            <legend>
              {t('regie:assistantReponse.termes')} ({r.termesSensibles.length} / 50)
            </legend>
            <p id="assistant-termes-bornes">{t('regie:assistantReponse.termesBornes')}</p>
            <ul>
              {r.termesSensibles.map((s, i) => (
                <li key={i} className={assistantStyles.terme}>
                  <span>{s}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label={t('regie:assistantReponse.retirerTerme', { terme: s })}
                    onClick={() =>
                      changer(
                        'termesSensibles',
                        r.termesSensibles.filter((_, index) => index !== i),
                      )
                    }
                  >
                    {t('regie:boiteReception.retirer')}
                  </Button>
                </li>
              ))}
            </ul>
            <div className={assistantStyles.terme}>
              <Field htmlFor="assistant-terme" label={t('regie:assistantReponse.nouveauTerme')}>
                <Input
                  id="assistant-terme"
                  maxLength={60}
                  value={terme}
                  aria-describedby="assistant-termes-bornes assistant-terme-borne"
                  onChange={(e) => setTerme(e.target.value)}
                />
                <small id="assistant-terme-borne">{terme.length} / 60</small>
              </Field>
              <Button
                type="button"
                variant="secondary"
                disabled={
                  !terme.trim() ||
                  r.termesSensibles.length >= 50 ||
                  r.termesSensibles.includes(terme.trim())
                }
                onClick={() => {
                  changer('termesSensibles', [...r.termesSensibles, terme.trim()]);
                  setTerme('');
                }}
              >
                {t('regie:assistantReponse.ajouterTerme')}
              </Button>
            </div>
          </fieldset>
          <fieldset className={styles.formulaire}>
            <legend>{t('regie:assistantReponse.reponsesAuto')}</legend>
            <Checkbox
              id="assistant-pratiques"
              label={t('regie:assistantReponse.pratiquesActives')}
              checked={r.reponsesPratiques.actif}
              onCheckedChange={(v) =>
                changer('reponsesPratiques', { ...r.reponsesPratiques, actif: v === true })
              }
            />
            {SUJETS_PRATIQUES.map((s) => (
              <Checkbox
                key={s}
                id={`assistant-sujet-${s}`}
                label={t(`regie:assistantReponse.sujets.${s}`)}
                checked={r.reponsesPratiques.sujets.includes(s)}
                onCheckedChange={(v) =>
                  changer('reponsesPratiques', {
                    ...r.reponsesPratiques,
                    sujets: v
                      ? [...r.reponsesPratiques.sujets, s]
                      : r.reponsesPratiques.sujets.filter((x) => x !== s),
                  })
                }
              />
            ))}
          </fieldset>
          <fieldset className={styles.formulaire}>
            <legend>{t('regie:assistantReponse.relances')}</legend>
            {(['actif', 'offres', 'vernissages'] as const).map((c) => (
              <Checkbox
                key={c}
                id={`assistant-relances-${c}`}
                label={t(`regie:assistantReponse.reglagesRelances.${c}`)}
                checked={r.relances[c]}
                onCheckedChange={(v) => changer('relances', { ...r.relances, [c]: v === true })}
              />
            ))}
            <Field htmlFor="assistant-delai" label={t('regie:assistantReponse.delai')}>
              <Input
                id="assistant-delai"
                type="number"
                required
                min={3}
                max={30}
                step={1}
                value={r.relances.delaiJours}
                onChange={(e) =>
                  changer('relances', { ...r.relances, delaiJours: Number(e.target.value) })
                }
              />
            </Field>
          </fieldset>
          <Button
            type="submit"
            disabled={
              pending ||
              Boolean(terme.trim()) ||
              (r.reponsesPratiques.actif && r.reponsesPratiques.sujets.length === 0)
            }
          >
            {t('commun:actions.enregistrer')}
          </Button>
        </fieldset>
      </form>
      <Dialog
        open={confirmation}
        onOpenChange={(v) => {
          if (!pending) setConfirmation(v);
        }}
        title={t('regie:assistantReponse.confirmationTitre')}
        description={t('regie:assistantReponse.confirmationTexte', { sujets })}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            disabled={pending}
            onClick={() => {
              void sauver({ ...r, confirmationAutomatique: true }).then((ok) => {
                if (ok) setConfirmation(false);
              });
            }}
          >
            {t('regie:assistantReponse.confirmer')}
          </Button>
        }
      >
        <p>{t('regie:assistantReponse.confirmationLimites')}</p>
      </Dialog>
    </>
  );
}
